"use client";
import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ListChecks, CheckCircle2, Loader2, Clock3, Gauge, Target, AlertTriangle, Zap } from "lucide-react";
import { computeTaskStats, PIE_COLORS } from "./performanceUtils";
import { CDN_URL } from "../../config/apiConfig";

const getInitials = (firstName, lastName) => {
  const f = firstName ? firstName[0].toUpperCase() : "";
  const l = lastName ? lastName[0].toUpperCase() : "";
  return f + l || "?";
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent} shrink-0`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-xl font-bold text-gray-800 leading-tight">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-lg px-3 py-2 text-xs">
      {label && <p className="font-semibold text-gray-700 mb-1">{label}</p>}
      {payload.map((entry) => (
        <p key={entry.name} className="text-gray-600 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: entry.color || entry.fill }} />
          {entry.name}: <span className="font-semibold text-gray-800">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function PerformanceDashboard({ tasks = [], title = "Performance Dashboard", employee = null }) {
  const stats = useMemo(() => computeTaskStats(tasks), [tasks]);

  return (
    <div className="bg-white p-5 rounded-2xl shadow space-y-5">
      <div className="flex items-center gap-2.5">
        {employee ? (
          employee.image ? (
            <img
              src={`${CDN_URL}/${employee.image}`}
              alt={`${employee.first_name || ""} ${employee.last_name || ""}`}
              className="w-9 h-9 rounded-xl object-cover shrink-0 ring-2 ring-gray-100"
            />
          ) : (
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-semibold shrink-0"
              style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
            >
              {getInitials(employee.first_name, employee.last_name)}
            </div>
          )
        ) : (
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
            style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
          >
            <Gauge size={18} />
          </div>
        )}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 leading-tight">{title}</h2>
          {employee?.department_name && (
            <p className="text-xs text-gray-500 mt-0.5">{employee.department_name}</p>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={ListChecks} label="Total Tasks" value={stats.total} accent="bg-gradient-to-br from-blue-500 to-indigo-600" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats.completedCount} accent="bg-gradient-to-br from-green-500 to-emerald-600" />
        <StatCard icon={Loader2} label="In Progress" value={stats.inProgressCount} accent="bg-gradient-to-br from-orange-400 to-amber-600" />
        <StatCard icon={Target} label="In Review" value={stats.reviewCount} accent="bg-gradient-to-br from-teal-500 to-cyan-600" />
        <StatCard icon={Clock3} label="Logged Hours" value={stats.loggedHours} accent="bg-gradient-to-br from-cyan-500 to-blue-600" />
        <StatCard icon={Zap} label="Estimated Hours" value={stats.estimatedHours} accent="bg-gradient-to-br from-violet-500 to-purple-600" />
        <StatCard icon={AlertTriangle} label="Overdue Tasks" value={stats.overdueCount} accent="bg-gradient-to-br from-red-500 to-rose-600" />
        <StatCard icon={Gauge} label="Efficiency" value={`${stats.efficiency}%`} accent="bg-gradient-to-br from-pink-500 to-rose-600" />
      </div>

      {/* Completion rate */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600">Completion Rate</span>
          <span className="text-xs font-semibold text-gray-700">
            {stats.completedCount}/{stats.total} ({stats.completionRate}%)
          </span>
        </div>
        <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: `${stats.completionRate}%`, background: "linear-gradient(135deg, #2563eb, #d12465)" }}
          />
        </div>
      </div>

      {/* Charts */}
      {stats.total > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <h3 className="text-xs font-semibold text-gray-600 mb-2">Tasks by Status</h3>
            <ResponsiveContainer width="100%" height={180} debounce={200}>
              <PieChart>
                <Pie
                  data={stats.byStatus}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {stats.byStatus.map((entry, index) => (
                    <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 mt-2">
              {stats.byStatus.map((item, idx) => (
                <span
                  key={item.name}
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-600 bg-gray-50 px-2 py-1 rounded-full"
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }} />
                  {item.name} <span className="text-gray-400">({item.value})</span>
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-600 mb-2">Tasks by Priority</h3>
            <ResponsiveContainer width="100%" height={180} debounce={200}>
              <BarChart data={stats.byPriority}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} isAnimationActive={false} cursor={{ fill: "rgba(37, 99, 235, 0.06)" }} />
                <Bar dataKey="value" name="Tasks" fill="#2563eb" radius={[6, 6, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
